<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { components } from '~/generated/types.d.ts'
import { to } from '~/routing'
import { computed } from 'vue'

import PlayButton from '~/components/audio/PlayButton.vue'
import Card from '~/ui/Card.vue'
import Spacer from '~/ui/Spacer.vue'

const { t } = useI18n()

const props = defineProps<{
  artist: components['schemas']['ArtistWithAlbums']
}>()

const cover_url = computed(() =>
  (props.artist.cover ?? props.artist.albums?.find(album => album.cover)?.cover)
    ?.urls.medium_square_crop
)

</script>

<template>
  <Card
    :title="artist.name"
    :tags="artist.tags"
    v-bind="to({name: 'library.artists.detail', params: {id: artist.id}})"
    small
    :ui-image-round="artist.content_category !== 'podcast'"
  >
    <template #topright>
      <PlayButton
        icon-only
        is-playable
        :artist
      />
    </template>

    <template #image="{ circle }">
      <img
        v-if="cover_url"
        v-bind="circle()"
        v-lazy="cover_url"
        :alt="artist.name"
        :style="artist.content_category === 'podcast' ? 'width: 100%' : undefined"
      >
      <Icon
        v-else
        v-bind="circle()"
        icon="bi:person-circle"
        style="width: 100%; height: 100%;"
      />
    </template>

    <template #footer>
      <span class="font-small layout-row" ui-block-size="40" style="padding-inline-start: 16px;">
        <span v-if="artist.content_category === 'music' && 'tracks_count' in artist">
          {{ t('components.audio.artist.Card.meta.tracks', artist.tracks_count) }}
        </span>
        <span v-else-if="'tracks_count' in artist">
          {{ t('components.audio.artist.Card.meta.episodes', artist.tracks_count) }}
        </span>
        <Icon
          v-if="artist.albums.length>0"
          icon="bi:dot"
        />
        <span v-if="artist.albums.length > 0">
          {{ t('components.audio.artist.Card.meta.albums', artist.albums.length) }}
        </span>
      </span>
      <Spacer grow />
      <PlayButton
        dropdown-only
        :is-playable="artist.is_playable"
        :artist
        discrete
      />
    </template>
  </Card>
</template>
